import type {
  ApiShortcutJSON,
  ApiShortcutTargetType,
} from '@/mastodon/api_types/shortcuts';
import { createAppSelector } from '@/mastodon/store/typed_functions';

const isDefined = (
  shortcut: ApiShortcutJSON | undefined,
): shortcut is ApiShortcutJSON => !!shortcut;

export const selectShortcuts = createAppSelector(
  [(state) => state.shortcuts.items, (state) => state.shortcuts.order],
  (items, order) => order.map((id) => items[id]).filter(isDefined),
);

export const selectShortcutsStatus = createAppSelector(
  [(state) => state.shortcuts.status],
  (status) => status,
);

const targetIdOf = (shortcut: ApiShortcutJSON): string | undefined => {
  switch (shortcut.target_type) {
    case 'account':
      return shortcut.account?.id;
    case 'group':
      return shortcut.group?.id;
    case 'chat_room':
      return shortcut.chat_room?.id;
    default:
      return undefined;
  }
};

// Used by the star toggle button on a profile/group/chat page to know
// whether the thing it's showing is already saved, and if so, which
// shortcut row to delete to remove it.
export const selectShortcutFor = createAppSelector(
  [
    (state) => state.shortcuts.items,
    (state) => state.shortcuts.order,
    (_state, targetType: ApiShortcutTargetType) => targetType,
    (_state, _targetType: ApiShortcutTargetType, targetId: string) => targetId,
  ],
  (items, order, targetType, targetId) =>
    order
      .map((id) => items[id])
      .filter(isDefined)
      .find(
        (shortcut) =>
          shortcut.target_type === targetType &&
          targetIdOf(shortcut) === targetId,
      ),
);
